import { validate } from 'validate.js';
import { bucketNameRegexes } from 'portal-shared-lib';

export enum ValidatorState {
    Inactive,
    Pass,
    Fail,
    InactivePass
}

export abstract class Validator {
    protected key: string;
    protected description: string | JSX.Element;
    protected canBeEmpty: boolean;

    private state: ValidatorState = ValidatorState.Inactive;

    getKey(): string { return this.key; }
    getDescription(): string | JSX.Element { return this.description; }

    getState(): ValidatorState {
        return this.state;
    }

    abstract validate(value: string): void;

    protected setState(state: ValidatorState): void {
        this.state = state;
    }
}

export class NameValidationService {
    private validators: Validator[];

    private value: string;

    constructor(validators: Validator[], value: string) {
        this.validators = validators;
        this.value = value ? value : '';
    }

    public isValid = (): boolean => {
        let result: boolean = true;
        for (let validator of this.validators) {
            validator.validate(this.value);
            if (
                validator.getState() === ValidatorState.Fail ||
                validator.getState() === ValidatorState.Inactive
            ) {
                return false;
            }
        }
        return result;
    }
}

abstract class BaseStringValidator extends Validator {
    constructor(description: string | JSX.Element, canBeEmpty: boolean = false) {
        super();
        this.description = description;
        this.key = this.constructor.toString();
        this.canBeEmpty = canBeEmpty;
    }

    validate(value: string): void {
        if (value.length === 0) {
            if (this.canBeEmpty) {
                this.setState(ValidatorState.InactivePass);
            } else {
                this.setState(ValidatorState.Inactive);
            }
        } else {
            this.setState(this.validateString(value));
        }
    }

    abstract validateString(value: string): ValidatorState;
}

export class UniqueNameValidator extends BaseStringValidator {
    nameSet: string[];

    constructor(description: string, nameSet: string[], canBeEmpty: boolean = false) {
        super(description, canBeEmpty);
        this.nameSet = nameSet;
    }

    validateString(value: string): ValidatorState {
        return this.nameSet.filter(name => name === value).length === 0 ?
            ValidatorState.Pass :
            ValidatorState.Fail;
    }
}

export class StartEndValidator extends BaseStringValidator {
    constructor(description: string | null = null, canBeEmpty: boolean = false) {
        let finalDescription = description ? description : 'Must start and end with a lowercase letter or number';
        super(finalDescription, canBeEmpty);
    }

    validateString(value: string): ValidatorState {
        return (bucketNameRegexes.lowercaseStartEnd).test(value) ?
            ValidatorState.Pass :
            ValidatorState.Fail;
    }
}

export class MixedcaseStartEndValidator extends BaseStringValidator {
    constructor(description: string | null = null, canBeEmpty: boolean = false) {
        let finalDescription = description ? description : 'Must start and end with a letter or number';
        super(finalDescription, canBeEmpty);
    }

    validateString(value: string): ValidatorState {
        return (new RegExp('^[A-Za-z0-9](.*[A-Za-z0-9])?$')).test(value) ?
            ValidatorState.Pass :
            ValidatorState.Fail;
    }
}

export class StringLengthValidator extends BaseStringValidator {
    maxStringLength: number | null;
    minStringLength: number;

    private minLimitPassedOnce: boolean = false;

    constructor(minStringLength: number, maxStringLength: number | null = null, description: string | JSX.Element = '', canBeEmpty: boolean = false) {
        super(description, canBeEmpty);

        let finalDescription: string | JSX.Element;
        if (description) {
            finalDescription = description;
        } else {
            if (maxStringLength !== null) {
                finalDescription = `Must be at least ${minStringLength} and no more than ${maxStringLength} characters long`;
            } else {
                finalDescription = `Must be at least ${minStringLength} characters long`;
            }
        }

        super(finalDescription, canBeEmpty);

        this.maxStringLength = maxStringLength;
        this.minStringLength = minStringLength;
    }

    validateString(value: string): ValidatorState {
        if (value.length >= this.minStringLength) {
            this.minLimitPassedOnce = true;
        }

        if (value.length < this.minStringLength) {
            if (this.minLimitPassedOnce) {
                return ValidatorState.Fail;
            }
            return ValidatorState.Inactive;
        } else {
            if (this.maxStringLength && value.length > this.maxStringLength) {
                return ValidatorState.Fail;
            }
            return ValidatorState.Pass;
        }
    }
}

export class LowercaseLettersNumbersDotsHyphensValidator extends BaseStringValidator {
    constructor(description: string | null = null, canBeEmpty: boolean = false) {
        let finalDescription = description ? description : 'Must contain only lowercase letters, numbers, dots or hyphens';
        super(finalDescription, canBeEmpty);
    }

    validateString(value: string): ValidatorState {
        let regex = bucketNameRegexes.lowercaseLettersNumbersDotsHyphens;
        return regex.test(value) ?
            ValidatorState.Pass :
            ValidatorState.Fail;
    }
}

export class LowercaseLettersNumbersHyphensValidator extends BaseStringValidator {
    constructor(description: string | null = null, canBeEmpty: boolean = false) {
        let finalDescription = description ? description : 'Must contain only lowercase letters, numbers or hyphens';
        super(finalDescription, canBeEmpty);
    }

    validateString(value: string): ValidatorState {
        let regex = new RegExp('^[a-z0-9-]+$');
        return regex.test(value) ?
            ValidatorState.Pass :
            ValidatorState.Fail;
    }
}

export class LettersNumbersHyphensValidator extends BaseStringValidator {
    constructor(description: string | null = null, canBeEmpty: boolean = false) {
        let finalDescription = description ? description : 'Must contain only letters, numbers or hyphens';
        super(finalDescription, canBeEmpty);
    }

    validateString(value: string): ValidatorState {
        let regex = new RegExp('^[A-Za-z0-9-]+$');
        return regex.test(value) ?
            ValidatorState.Pass :
            ValidatorState.Fail;
    }
}

export class NoDashSequenceValidator extends BaseStringValidator {
    constructor(description: string | null = null, canBeEmpty: boolean = false) {
        let finalDescription = description ?
            description :
            'Must not contain consecutive hyphens';
        super(finalDescription, canBeEmpty);
    }

    validateString(value: string): ValidatorState {
        // No consecutive dots or any form of a dash dot sequence
        let regex = new RegExp('--'); // bucketNameRegexes.dashDotSequence;
        return !regex.test(value) ?
            ValidatorState.Pass :
            ValidatorState.Fail;
    }
}

export class NoIpAddressValidator extends BaseStringValidator {
    constructor(description: string | null = null, canBeEmpty: boolean = false) {
        let finalDescription = description ? description : 'Must not be formatted as an IP address';
        super(finalDescription, canBeEmpty);
    }

    validateString(value: string): ValidatorState {
        let regex = bucketNameRegexes.ipAddress;
        return !regex.test(value) ?
            ValidatorState.Pass :
            ValidatorState.Fail;
    }
}

export class NonEmptyValidator extends BaseStringValidator {
    constructor(description: string, canBeEmpty: boolean = false) {
        let finalDescription = description ? description : 'Must not be an empty string';
        super(finalDescription, canBeEmpty);
    }

    validateString(value: string): ValidatorState {
        return value !== '' ?
            ValidatorState.Pass :
            ValidatorState.Fail;
    }
}

export class UrlValidator extends BaseStringValidator {
    constructor(description: string | null, canBeEmpty: boolean = false) {
        let finalDescription = description ? description : 'Is a valid url';
        super(finalDescription, canBeEmpty);
    }

    public validateString(value: string): ValidatorState {
        return !validate({ website: value }, { website: { url: { allowLocal: true } } }) ?
            ValidatorState.Pass :
            ValidatorState.Fail;
    }
}



// WEBPACK FOOTER //
// ./src/webportal/services/nameValidation.tsx